import { FC, useCallback, useContext, useEffect, useMemo } from 'react';

import { Form as FormAntd, Table } from 'antd';

import {
  GenericDataFormContext,
  QuotationInfoContext,
} from '../../../../../../../shared';
import { ContentSection, SwitchForm } from '../../../../../../../../../../../shared/components';

import { StepThreeFormContext } from '../../../../contexts/StepThreeForm';

import { Feature } from '../../../../../../../../../../../shared/interfaces';

const MOCK_TABLE_COLUMNS_HEADER = [
  {
    title: 'Item',
    dataIndex: 'codeItem',
    width: '10%',
  },
  {
    title: 'Código',
    dataIndex: 'featureCode',
    width: '10%',
  },
  {
    title: 'Descripción de Características',
    dataIndex: 'featureDescription',
    width: '60%',
  },
  {
    title: '¿Tiene?',
    dataIndex: 'hasItem',
    width: '10%',
  },
];

interface TableFeaturesProps {
  duplicateId?: number;
  id: number;
}

export const TableFeatures: FC<TableFeaturesProps> = ({ duplicateId, id }) => {
  const { allFeatures, getFeaturesRisks, setAllFeatures, stepThreeForm } =
    useContext(StepThreeFormContext);
  const { setCanShowSpinner } = useContext(GenericDataFormContext);
  const { quotationToRenewal, savedQuotation } = useContext(QuotationInfoContext);
  const quotation = quotationToRenewal ?? savedQuotation;

  const planFormItemName = `plan-tab-${id}`;
  const planSelected = FormAntd.useWatch(planFormItemName, stepThreeForm);

  const getFeaturesRiskByPlan = useCallback(
    async (planSelected: string) => {
      if( allFeatures.find(el => String(el.planCode) === String(planSelected) )) return;

      setCanShowSpinner(true);

      try {
        const bien = quotation?.bienes?.[id - 1];
        const componente = Number(bien?.numeroComponente ?? id);

        const featuresList = await getFeaturesRisks(planSelected, componente);

        if (featuresList?.length) {
          setAllFeatures((prevValues) => [
            ...prevValues,
            { planCode: planSelected, keyTab: id, features: featuresList },
          ]);
        }
      } catch {
      } finally {
        setCanShowSpinner(false);
      }
    },
    [setCanShowSpinner, setAllFeatures, getFeaturesRisks, allFeatures, quotation, id]
  );

  const setFeaturesTable = useMemo(
    () =>
      allFeatures?.find((el) => el.planCode === planSelected)?.features.length
        ? allFeatures
            ?.find((el) => el.planCode === planSelected)
            ?.features?.map((feature: Feature) => ({
              codeItem: feature.codigo,
              featureCode: feature.codigoEquivalente,
              featureDescription: feature.descripcion,
              hasItem: (
                <SwitchForm
                  formProps={{
                    initialValue: feature.valorPorDefecto,
                    name: `feature-tab-${id}-${feature.codigo}-${feature.codigoEquivalente}`,
                  }}
                  switchProps={{
                    disabled: !feature.permiteModificar,
                  }}
                />
              ),
              key: feature.codigo,
            }))
        : [],
    [allFeatures, planSelected]
  );

  const duplicateFieldsValues = () => {
    if (duplicateId) {
      const allFieldsValues = stepThreeForm?.getFieldsValue(true);

      const duplicatedFieldsValues = Object.keys(allFieldsValues).reduce(
        (acc, key) => {
          if (key.includes(`feature-tab-${duplicateId}`)) {
            const newKey = key.replace(
              `feature-tab-${duplicateId}-`,
              `feature-tab-${id}-`
            );
            acc[newKey] = allFieldsValues[key];
          }

          return acc;
        },
        {} as Record<string, any>
      );

      stepThreeForm?.setFieldsValue(duplicatedFieldsValues);
    }
  };

  useEffect(() => {
    duplicateFieldsValues();
  }, [duplicateId, id, stepThreeForm]);

  useEffect(() => {
    if (planSelected) getFeaturesRiskByPlan(planSelected);
  }, [planSelected, getFeaturesRiskByPlan]);

  return (
    <ContentSection level='h3' title='Características' hasCard={false}>
      <Table
        bordered
        className='person-table'
        columns={MOCK_TABLE_COLUMNS_HEADER}
        dataSource={setFeaturesTable}
        pagination={false}
        scroll={{ x: 'auto' }}
      />
    </ContentSection>
  );
};
